import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Fade,
  Chip,
  Paper,
} from '@mui/material';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  AccountBalance as AccountIcon,
  TrendingUp as TrendingUpIcon,
  MonetizationOn as MonetizationIcon,
  CalendarMonth as CalendarIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import apiClient from '../api/apiClient';
import ResponsiveAppBar from '../components/AppBar';

// Register Chart.js components and plugins
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// Create theme with specified color scheme
const theme = createTheme({
  palette: {
    primary: { main: '#77c8c7' }, // Teal
    secondary: { main: '#936097' }, // Plum
    background: { default: '#f8fafc', paper: '#ffffff' },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
  },
  typography: {
    fontFamily: '"Outfit", "Poppins", "Roboto", sans-serif',
    h4: {
      fontWeight: 800,
      letterSpacing: '-0.5px',
      color: '#1e293b',
    },
    subtitle1: {
      fontWeight: 500,
      color: '#64748b',
      fontSize: '1.05rem',
    },
  },
  shape: {
    borderRadius: 14,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.03)',
          border: '1px solid rgba(226, 232, 240, 0.9)',
          background: '#ffffff',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            background: '#ffffff',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#77c8c7',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#936097',
            },
          },
        },
      },
    },
  },
});

function MonthlyAccountsGraphPage() {
  const navigate = useNavigate();
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });
  const [selectedPage, setSelectedPage] = useState('');
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [selectedFields, setSelectedFields] = useState(['totalLoanAmount', 'totalDueAmountPaid']);
  const [selectedChartType, setSelectedChartType] = useState('bar');
  const [openMonths, setOpenMonths] = useState(false);
  const [openWeeks, setOpenWeeks] = useState(false);
  const [openFields, setOpenFields] = useState(false);
  const [openChartType, setOpenChartType] = useState(false);

  // Fetch all summaries on mount
  useEffect(() => {
    fetchSummaries();
  }, []);

  const fetchSummaries = async () => {
    setLoading(true);
    try {
      const data = await apiClient.getAllSummaries();
      const sorted = (data || []).sort((a, b) => {
        const dateA = new Date(
          a.summaryMonth.split(' ')[1],
          new Date(Date.parse(a.summaryMonth.split(' ')[0] + ' 1, 2000')).getMonth()
        );
        const dateB = new Date(
          b.summaryMonth.split(' ')[1],
          new Date(Date.parse(b.summaryMonth.split(' ')[0] + ' 1, 2000')).getMonth()
        );
        return dateA - dateB;
      });
      setSummaries(sorted);
      if (sorted.length > 0) {
        setSelectedMonths([sorted[sorted.length - 1].summaryMonth]);
      }
      setLoading(false);
    } catch (err) {
      showAlert(err.message || 'Error fetching data', 'error');
      setLoading(false);
    }
  };

  const handleNavigate = (event) => {
    const page = event.target.value;
    setSelectedPage(page);
    if (page) {
      navigate(page);
    }
  };

  const handleMonthChange = (event, month) => {
    if (month === 'all') {
      if (selectedMonths.length === summaries.length) {
        setSelectedMonths([]);
      } else {
        setSelectedMonths(summaries.map((s) => s.summaryMonth));
      }
    } else {
      setSelectedMonths((prev) =>
        prev.includes(month)
          ? prev.filter((m) => m !== month)
          : [...prev, month]
      );
    }
    setSelectedWeeks([]);
    setOpenMonths(false);
  };

  const handleWeekChange = (event, week) => {
    if (week === 'all') {
      const allWeeks = Array.from(
        new Set(
          summaries
            .filter((s) => selectedMonths.includes(s.summaryMonth))
            .flatMap((summary) => groupEntriesByWeek(summary.dailyEntries).map((w) => String(w.weekNumber)))
        )
      );
      if (selectedWeeks.length === allWeeks.length) {
        setSelectedWeeks([]);
      } else {
        setSelectedWeeks(allWeeks);
      }
    } else {
      setSelectedWeeks((prev) =>
        prev.includes(week)
          ? prev.filter((w) => w !== week)
          : [...prev, week]
      );
    }
    setOpenWeeks(false);
  };

  const handleFieldChange = (event, field) => {
    if (field === 'all') {
      const allFields = availableFields.filter((f) => f.value !== 'lendedAndDue').map((f) => f.value);
      if (selectedFields.length === allFields.length) {
        setSelectedFields([]);
      } else {
        setSelectedFields(allFields);
      }
    } else if (field === 'lendedAndDue') {
      const hasBoth = selectedFields.includes('totalLoanAmount') && selectedFields.includes('totalDueAmountPaid');
      if (hasBoth) {
        setSelectedFields((prev) => prev.filter((f) => f !== 'totalLoanAmount' && f !== 'totalDueAmountPaid'));
      } else {
        setSelectedFields((prev) => Array.from(new Set([...prev, 'totalLoanAmount', 'totalDueAmountPaid'])));
      }
    } else {
      setSelectedFields((prev) =>
        prev.includes(field)
          ? prev.filter((f) => f !== field)
          : [...prev, field]
      );
    }
    setOpenFields(false);
  };

  const handleChartTypeChange = (event) => {
    setSelectedChartType(event.target.value);
    setOpenChartType(false);
  };

  const showAlert = (message, severity) => {
    setAlert({ open: true, message, severity });
  };

  const groupEntriesByWeek = (dailyEntries = []) => {
    const weeks = [];
    const sortedEntries = [...dailyEntries].sort((a, b) => {
      const dayA = parseInt(a.date.split('-')[0]);
      const dayB = parseInt(b.date.split('-')[0]);
      return dayA - dayB;
    });

    for (let i = 0; i < sortedEntries.length; i += 7) {
      const weekEntries = sortedEntries.slice(i, i + 7);
      if (weekEntries.length > 0) {
        const startDay = parseInt(weekEntries[0].date.split('-')[0]);
        const endDay = parseInt(weekEntries[weekEntries.length - 1].date.split('-')[0]);
        const weekData = {
          weekNumber: Math.floor(i / 7) + 1,
          startDay,
          endDay,
          totalFundReleasedToF2F: 0,
          totalNoOfLoans: 0,
          totalLoanAmount: 0,
          totalPrincipalInDue: 0,
          totalInterestInDue: 0,
          totalDueAmountPaid: 0,
          totalPendingDue: 0,
          totalRefreshmentExpenses: 0,
          totalOtherExpenses: 0,
          totalDocCharge: 0,
          totalClosingBalance: 0,
        };

        weekEntries.forEach((entry) => {
          weekData.totalFundReleasedToF2F += entry.fundReleasedToF2F || 0;
          weekData.totalNoOfLoans += entry.noOfLoans || 0;
          weekData.totalLoanAmount += entry.loanAmount || 0;
          weekData.totalPrincipalInDue += entry.principalInDue || 0;
          weekData.totalInterestInDue += entry.interestInDue || 0;
          weekData.totalDueAmountPaid += entry.dueAmountPaid || 0;
          weekData.totalPendingDue += entry.pendingDue || 0;
          weekData.totalRefreshmentExpenses += entry.refreshmentExpenses || 0;
          weekData.totalOtherExpenses += entry.otherExpenses || 0;
          weekData.totalDocCharge += entry.docCharge || 0;
          weekData.totalClosingBalance += entry.closingBalance || 0;
        });

        weeks.push(weekData);
      }
    }
    return weeks;
  };

  // Color palette for fields
  const fieldColors = {
    totalLoanAmount: { main: '#77c8c7', light: 'rgba(119, 200, 199, 0.4)' },
    totalDueAmountPaid: { main: '#936097', light: 'rgba(147, 96, 151, 0.4)' },
    totalPrincipalInDue: { main: '#0ea5e9', light: 'rgba(14, 165, 233, 0.4)' },
    totalInterestInDue: { main: '#f59e0b', light: 'rgba(245, 158, 11, 0.4)' },
    totalFundReleasedToF2F: { main: '#10b981', light: 'rgba(16, 185, 129, 0.4)' },
    totalNoOfLoans: { main: '#8b5cf6', light: 'rgba(139, 92, 246, 0.4)' },
    totalPendingDue: { main: '#ef4444', light: 'rgba(239, 68, 68, 0.4)' },
    totalRefreshmentExpenses: { main: '#ec4899', light: 'rgba(236, 72, 153, 0.4)' },
    totalOtherExpenses: { main: '#64748b', light: 'rgba(100, 116, 139, 0.4)' },
    totalDocCharge: { main: '#14b8a6', light: 'rgba(20, 184, 166, 0.4)' },
    totalClosingBalance: { main: '#6366f1', light: 'rgba(99, 102, 241, 0.4)' },
  };

  const getUnifiedChartData = () => {
    let labels = [];
    const datasets = [];

    if (selectedWeeks.length > 0) {
      selectedMonths.forEach((month) => {
        const summary = summaries.find((s) => s.summaryMonth === month);
        if (summary) {
          const weeks = groupEntriesByWeek(summary.dailyEntries);
          selectedWeeks.forEach((weekNum) => {
            const week = weeks.find((w) => w.weekNumber === parseInt(weekNum));
            if (week) {
              labels.push(`${month} - Wk ${week.weekNumber} (${week.startDay}-${week.endDay})`);
            }
          });
        }
      });

      selectedFields.forEach((fieldKey) => {
        const fieldConfig = availableFields.find((f) => f.value === fieldKey);
        const data = [];
        selectedMonths.forEach((month) => {
          const summary = summaries.find((s) => s.summaryMonth === month);
          if (summary) {
            const weeks = groupEntriesByWeek(summary.dailyEntries);
            selectedWeeks.forEach((weekNum) => {
              const week = weeks.find((w) => w.weekNumber === parseInt(weekNum));
              data.push(week ? week[fieldKey] || 0 : 0);
            });
          }
        });

        const color = fieldColors[fieldKey] || { main: '#77c8c7', light: 'rgba(119, 200, 199, 0.4)' };
        datasets.push({
          label: fieldConfig ? fieldConfig.label : fieldKey,
          data,
          backgroundColor: selectedChartType === 'line' ? color.light : color.main,
          borderColor: color.main,
          borderWidth: 2.5,
          borderRadius: 6,
          fill: selectedChartType === 'line',
          tension: 0.35,
        });
      });
    } else {
      labels = selectedMonths;
      selectedFields.forEach((fieldKey) => {
        const fieldConfig = availableFields.find((f) => f.value === fieldKey);
        const data = selectedMonths.map((month) => {
          const summary = summaries.find((s) => s.summaryMonth === month);
          return summary ? summary[fieldKey] || 0 : 0;
        });

        const color = fieldColors[fieldKey] || { main: '#77c8c7', light: 'rgba(119, 200, 199, 0.4)' };
        datasets.push({
          label: fieldConfig ? fieldConfig.label : fieldKey,
          data,
          backgroundColor: selectedChartType === 'line' ? color.light : color.main,
          borderColor: color.main,
          borderWidth: 2.5,
          borderRadius: 6,
          fill: selectedChartType === 'line',
          tension: 0.35,
        });
      });
    }

    return { labels, datasets };
  };

  const getChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: { family: 'Outfit, sans-serif', weight: '600', size: 12 },
          color: '#334155',
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
        },
      },
      datalabels: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#ffffff',
        titleColor: '#1e293b',
        bodyColor: '#475569',
        borderColor: 'rgba(226, 232, 240, 0.9)',
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const val = context.raw || 0;
            const isCount = label.includes('No.') || label.includes('Loans');
            return isCount ? `${label}: ${val}` : `${label}: ₹${Number(val).toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { font: { family: 'Outfit, sans-serif', weight: '500' }, color: '#64748b' },
        grid: { display: false },
      },
      y: {
        ticks: {
          callback: (value) => `₹${(value / 1000).toFixed(0)}k`,
          font: { family: 'Outfit, sans-serif', weight: '500' },
          color: '#64748b',
        },
        grid: { color: 'rgba(226, 232, 240, 0.6)' },
      },
    },
  });

  const availableFields = [
    { value: 'totalLoanAmount', label: 'Lended Loan Amount' },
    { value: 'totalDueAmountPaid', label: 'Due Amount Collected' },
    { value: 'totalPrincipalInDue', label: 'Principal In Due' },
    { value: 'totalInterestInDue', label: 'Interest In Due' },
    { value: 'totalFundReleasedToF2F', label: 'Fund Released to F2F' },
    { value: 'totalNoOfLoans', label: 'No. of Loans' },
    { value: 'totalPendingDue', label: 'Pending Dues' },
    { value: 'totalDocCharge', label: 'Documentation Charges' },
    { value: 'totalClosingBalance', label: 'Closing Balance' },
    { value: 'totalRefreshmentExpenses', label: 'Refreshment Expenses' },
    { value: 'totalOtherExpenses', label: 'Other Operational Expenses' },
    { value: 'lendedAndDue', label: 'Lended & Collected (Combo)' },
  ];

  const chartTypes = [
    { value: 'bar', label: 'Bar Chart View' },
    { value: 'line', label: 'Line Trend View' },
  ];

  // Summary Metrics Calculation
  const totalLendedAll = summaries.reduce((acc, curr) => acc + (curr.totalLoanAmount || 0), 0);
  const totalPaidAll = summaries.reduce((acc, curr) => acc + (curr.totalDueAmountPaid || 0), 0);
  const totalPrincipalDueAll = summaries.reduce((acc, curr) => acc + (curr.totalPrincipalInDue || 0), 0);

  return (
    <>
      <ResponsiveAppBar />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: { xs: 3, md: 5 } }}>
          <Container maxWidth="xl">
            {/* Header Section */}
            <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
              <Box>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.6, borderRadius: '50px', bgcolor: 'rgba(119, 200, 199, 0.15)', mb: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#77c8c7' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#936097', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    Visual Analytics & Performance
                  </Typography>
                </Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
                  Monthly Accounts Visual Analytics
                </Typography>
                <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                  Multi-metric trend inspection, month-over-month performance & weekly cash flow breakdown
                </Typography>
              </Box>

              <FormControl size="small" sx={{ minWidth: 220, bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <InputLabel sx={{ fontWeight: 500, color: '#64748b' }}>Navigate Module</InputLabel>
                <Select
                  value={selectedPage}
                  label="Navigate Module"
                  onChange={handleNavigate}
                  sx={{
                    borderRadius: 3,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(226, 232, 240, 0.9)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#77c8c7' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#936097' },
                  }}
                >
                  <MenuItem value="/admin">Gold & Vehicle Loan</MenuItem>
                  <MenuItem value="/velavan-finance">Velavan Finance Ledger</MenuItem>
                  <MenuItem value="/due-matters">Due Matters</MenuItem>
                  <MenuItem value="/interestCalculation">Interest Calculation</MenuItem>
                  <MenuItem value="/dailyAccounts">Daily Accounts</MenuItem>
                  <MenuItem value="/finalAccounts">Final Accounts</MenuItem>
                  <MenuItem value="/monthly-graph">Monthly Graph Status</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Top KPI Metrics */}
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3.5, bgcolor: '#ffffff', border: '1px solid rgba(119, 200, 199, 0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
                  <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(119, 200, 199, 0.15)', color: '#77c8c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <MonetizationIcon sx={{ fontSize: 26 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Total Lended Volume
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                        ₹{totalLendedAll.toLocaleString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3.5, bgcolor: '#ffffff', border: '1px solid rgba(147, 96, 151, 0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
                  <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(147, 96, 151, 0.12)', color: '#936097', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <TrendingUpIcon sx={{ fontSize: 26 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Total Collections
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#936097' }}>
                        ₹{totalPaidAll.toLocaleString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3.5, bgcolor: '#ffffff', border: '1px solid rgba(14, 165, 233, 0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
                  <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(14, 165, 233, 0.12)', color: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <AccountIcon sx={{ fontSize: 26 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Principal in Dues
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#0284c7' }}>
                        ₹{totalPrincipalDueAll.toLocaleString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3.5, bgcolor: '#ffffff', border: '1px solid rgba(34, 197, 94, 0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
                  <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(34, 197, 94, 0.12)', color: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CalendarIcon sx={{ fontSize: 26 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Recorded Months
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#15803d' }}>
                        {summaries.length} <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#64748b' }}>active cycles</span>
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Filter Controls Bar */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 4,
                borderRadius: 4,
                bgcolor: '#ffffff',
                border: '1px solid rgba(226, 232, 240, 0.9)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Filter Months</InputLabel>
                    <Select
                      multiple
                      value={selectedMonths}
                      label="Filter Months"
                      disabled={loading}
                      renderValue={(selected) => selected.join(', ')}
                      open={openMonths}
                      onOpen={() => setOpenMonths(true)}
                      onClose={() => setOpenMonths(false)}
                    >
                      <MenuItem value="all" onClick={() => handleMonthChange(null, 'all')}>
                        <Checkbox checked={selectedMonths.length === summaries.length} />
                        <ListItemText primary="All Months" />
                      </MenuItem>
                      {summaries.map((summary) => (
                        <MenuItem
                          key={summary.summaryMonth}
                          value={summary.summaryMonth}
                          onClick={() => handleMonthChange(null, summary.summaryMonth)}
                        >
                          <Checkbox checked={selectedMonths.includes(summary.summaryMonth)} />
                          <ListItemText primary={summary.summaryMonth} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Filter Weeks (Optional)</InputLabel>
                    <Select
                      multiple
                      value={selectedWeeks}
                      label="Filter Weeks (Optional)"
                      disabled={loading || selectedMonths.length === 0}
                      renderValue={(selected) => selected.map((w) => `Week ${w}`).join(', ')}
                      open={openWeeks}
                      onOpen={() => setOpenWeeks(true)}
                      onClose={() => setOpenWeeks(false)}
                    >
                      <MenuItem value="all" onClick={() => handleWeekChange(null, 'all')}>
                        <Checkbox
                          checked={
                            selectedWeeks.length ===
                            new Set(
                              summaries
                                .filter((s) => selectedMonths.includes(s.summaryMonth))
                                .flatMap((summary) => groupEntriesByWeek(summary.dailyEntries).map((w) => String(w.weekNumber)))
                            ).size
                          }
                        />
                        <ListItemText primary="All Weeks" />
                      </MenuItem>
                      {selectedMonths.length > 0 &&
                        summaries
                          .filter((s) => selectedMonths.includes(s.summaryMonth))
                          .flatMap((summary) =>
                            groupEntriesByWeek(summary.dailyEntries).map((week) => (
                              <MenuItem
                                key={week.weekNumber}
                                value={String(week.weekNumber)}
                                onClick={() => handleWeekChange(null, String(week.weekNumber))}
                              >
                                <Checkbox checked={selectedWeeks.includes(String(week.weekNumber))} />
                                <ListItemText primary={`Week ${week.weekNumber} (Days ${week.startDay}-${week.endDay})`} />
                              </MenuItem>
                            ))
                          )
                          .filter((v, i, a) => a.findIndex((t) => t.key === v.key) === i)}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Metrics & Fields</InputLabel>
                    <Select
                      multiple
                      value={selectedFields}
                      label="Metrics & Fields"
                      disabled={loading}
                      renderValue={(selected) =>
                        selected
                          .map((field) => availableFields.find((f) => f.value === field)?.label || field)
                          .join(', ')
                      }
                      open={openFields}
                      onOpen={() => setOpenFields(true)}
                      onClose={() => setOpenFields(false)}
                    >
                      <MenuItem value="all" onClick={() => handleFieldChange(null, 'all')}>
                        <Checkbox checked={selectedFields.length === availableFields.filter((f) => f.value !== 'lendedAndDue').length} />
                        <ListItemText primary="Select All Fields" />
                      </MenuItem>
                      <MenuItem value="lendedAndDue" onClick={() => handleFieldChange(null, 'lendedAndDue')}>
                        <Checkbox checked={selectedFields.includes('totalLoanAmount') && selectedFields.includes('totalDueAmountPaid')} />
                        <ListItemText primary="Lended & Collected (Quick Combo)" />
                      </MenuItem>
                      {availableFields
                        .filter((f) => f.value !== 'lendedAndDue')
                        .map((field) => (
                          <MenuItem
                            key={field.value}
                            value={field.value}
                            onClick={() => handleFieldChange(null, field.value)}
                          >
                            <Checkbox checked={selectedFields.includes(field.value)} />
                            <ListItemText primary={field.label} />
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Chart Visualization</InputLabel>
                    <Select
                      value={selectedChartType}
                      onChange={handleChartTypeChange}
                      label="Chart Visualization"
                      disabled={loading}
                      open={openChartType}
                      onOpen={() => setOpenChartType(true)}
                      onClose={() => setOpenChartType(false)}
                    >
                      {chartTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <ListItemText primary={type.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {/* Main Interactive Chart Card */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 4 },
                borderRadius: 4,
                bgcolor: '#ffffff',
                border: '1px solid rgba(226, 232, 240, 0.9)',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.04)',
                minHeight: 520,
              }}
            >
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: 'rgba(119, 200, 199, 0.15)', color: '#77c8c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <BarChartIcon />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    Financial Trends & Cash Flow Graph
                  </Typography>
                </Box>
                <Chip
                  label={`${selectedMonths.length} Selected Months • ${selectedFields.length} Metrics`}
                  sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 700, borderRadius: 2 }}
                />
              </Box>

              {loading ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 400,
                  }}
                >
                  <CircularProgress size={48} sx={{ color: '#77c8c7' }} />
                  <Typography variant="body2" sx={{ mt: 2, color: '#64748b', fontWeight: 600 }}>
                    Synthesizing monthly account charts...
                  </Typography>
                </Box>
              ) : selectedMonths.length > 0 && selectedFields.length > 0 ? (
                <Box sx={{ height: 440, width: '100%' }}>
                  {selectedChartType === 'line' ? (
                    <Line options={getChartOptions()} data={getUnifiedChartData()} />
                  ) : (
                    <Bar options={getChartOptions()} data={getUnifiedChartData()} />
                  )}
                </Box>
              ) : (
                <Box sx={{ py: 10, textAlign: 'center' }}>
                  <BarChartIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                  <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 600 }}>
                    Select Parameters to Render Graph
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    Pick at least one month and one metric field from the controls bar above.
                  </Typography>
                </Box>
              )}
            </Paper>

            <Snackbar
              open={alert.open}
              autoHideDuration={6000}
              onClose={() => setAlert({ ...alert, open: false })}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert
                onClose={() => setAlert({ ...alert, open: false })}
                severity={alert.severity}
                sx={{ width: '100%', borderRadius: 2.5, fontWeight: 600 }}
              >
                {alert.message}
              </Alert>
            </Snackbar>
          </Container>
        </Box>
      </ThemeProvider>
    </>
  );
}

export default MonthlyAccountsGraphPage;
